'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * SidebarContext - Shares sidebar collapse state across components
 */

interface SidebarContextType {
    isCollapsed: boolean;
    setIsCollapsed: (collapsed: boolean) => void;
    toggleCollapse: () => void;
}

const STORAGE_KEY = 'pms-sidebar-collapsed';

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: ReactNode }) {
    // Always start with false (expanded) on both server and client
    // to prevent hydration mismatch. Sync from localStorage in useEffect.
    const [isCollapsed, setIsCollapsed] = useState(false);
    
    // Track if client-side hydration is complete
    const [isHydrated, setIsHydrated] = useState(false);
    
    useEffect(() => {
        // Read from localStorage after mount (client-only)
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === 'true') {
            setIsCollapsed(true);
        }
        setIsHydrated(true);
    }, []);
    
    // Save collapsed state to localStorage
    const toggleCollapse = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        localStorage.setItem(STORAGE_KEY, String(newState));
    };
    
    const updateCollapsed = (collapsed: boolean) => {
        setIsCollapsed(collapsed);
        localStorage.setItem(STORAGE_KEY, String(collapsed));
    };

    return (
        <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed: updateCollapsed, toggleCollapse }}>
            {/* Hide content until hydrated to prevent flash */}
            <div style={{ visibility: isHydrated ? 'visible' : 'hidden' }}>
                {children}
            </div>
        </SidebarContext.Provider>
    );
}

export function useSidebar() {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
}

/**
 * For components that might be used outside SidebarProvider
 * Returns default values instead of throwing
 */
export function useSidebarOptional() {
    const context = useContext(SidebarContext);
    return context ?? { isCollapsed: false, setIsCollapsed: () => {}, toggleCollapse: () => {} };
}
